// src/pages/dashboard/AdminReviews.jsx
import React, { useEffect, useMemo, useState } from "react";
import { getReviewsPublic, deleteReviewAdmin } from "../../core/api/reviewsApi";

const snippet = (txt, n = 70) => {
  const s = String(txt || "");
  if (s.length <= n) return s;
  return s.slice(0, n) + "…";
};

export default function AdminReviews() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      setErr("");
      const data = await getReviewsPublic();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [rows]);

  const onDelete = async (r) => {
    const ok = window.confirm(`Delete review by ${r.name}? (Soft delete)`);
    if (!ok) return;
    try {
      await deleteReviewAdmin(r.id);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || "Delete failed");
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Reviews</h1>
        <p className="text-sm text-gray-600 mt-1">Manage testimonials (delete)</p>
      </div>

      <div className="mt-5">
        {loading && (
          <div className="rounded-2xl border border-gray-200 bg-white p-6 text-gray-600">
            Loading…
          </div>
        )}

        {!loading && err && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700">
            {err}
          </div>
        )}

        {!loading && !err && sorted.length === 0 && (
          <div className="rounded-2xl border border-gray-200 bg-white p-6 text-gray-600">
            No reviews found.
          </div>
        )}

        {!loading && !err && sorted.length > 0 && (
          <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Name</th>
                  <th className="text-left px-4 py-3 font-semibold">Organization</th>
                  <th className="text-left px-4 py-3 font-semibold">Product</th>
                  <th className="text-left px-4 py-3 font-semibold">Review</th>
                  <th className="text-left px-4 py-3 font-semibold">Created</th>
                  <th className="text-right px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {sorted.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3 font-semibold text-gray-900">{r.name || "—"}</td>
                    <td className="px-4 py-3 text-gray-700">{r.organization || "—"}</td>
                    <td className="px-4 py-3 text-gray-700">{r.product_name || "—"}</td>
                    <td className="px-4 py-3 text-gray-700">{snippet(r.review)}</td>
                    <td className="px-4 py-3 text-gray-700">
                      {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end">
                        <button
                          onClick={() => onDelete(r)}
                          className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
